// Page registry for the retailer portal.
//
// Each real page module side-effect-registers itself with the router via
// `registerPage(...)`. Pages not yet extracted are represented by stubs
// defined here. After everything has registered, `setOrder` is called to
// enforce the canonical sidebar order regardless of import-hoisting.

import { registerPage, getPage, setOrder } from "./router";
import type { Page, PageContext, PageCleanup } from "./types";

// (The previous "home/welcome" placeholder page was removed: dashboard
// is the only landing page. The router resolves any unknown / hashless
// route to the runtime defaultPage — wired to "dashboard" in shell.ts.)

// ── Real pages — side-effect imports register themselves ────────────────────
// These run before the rest of this file (ES module imports are hoisted).
// `setOrder` at the bottom of the file fixes the sidebar order regardless.
import "../pages/bookings/page";
import "../pages/calendar/page";
import "../pages/catalogue-manager/page";
import "../pages/contacts/page";
import "../pages/crm/page";
import "../pages/customer/page";
import "../pages/dashboard/page";
import "../pages/deliveries/page";
import "../pages/designs/page";
import "../pages/drivers/page";
import "../pages/feedback/page";
import "../pages/help/page";
import "../pages/messages/page";
import "../pages/operations/page";
import "../pages/payments/page";
import "../pages/product-search/page";
import "../pages/quote-builder/index";
import "../pages/reports/page";
import "../pages/roadmap/page";
import "../pages/settings/index";
import "../pages/social-ai/page";
import "../pages/submissions/page";
import "../pages/suppliers/page";

// ── Stub specs ──────────────────────────────────────────────────────────────
// Stubs are only registered if a real page hasn't been registered already.
// The order here is the canonical sidebar order (enforced by `setOrder`).

interface StubSpec {
  name: string;
  label: string;
  icon: string;
  hidden?: boolean;
}

const STUBS: StubSpec[] = [
  { name: "dashboard",         label: "Dashboard",            icon: "" },
  { name: "submissions",       label: "Submissions",          icon: "", hidden: true },
  { name: "crm",               label: "CRM & Comms",          icon: "" },
  { name: "customer",          label: "Customer",             icon: "", hidden: true },
  { name: "messages",          label: "Messages",             icon: "" },
  { name: "bookings",          label: "Bookings",             icon: "" },
  { name: "designs",           label: "Designs",              icon: "" },
  { name: "contacts",          label: "Contacts",             icon: "" },
  { name: "quote-builder",     label: "Quote Builder",        icon: "", hidden: true },
  { name: "calendar",          label: "Calendar",             icon: "" },
  { name: "operations",        label: "Operations",           icon: "" },
  { name: "deliveries",        label: "Deliveries",           icon: "" },
  { name: "drivers",           label: "Manage Drivers",       icon: "" },
  { name: "suppliers",         label: "Suppliers",            icon: "" },
  { name: "ai",                label: "AI Tools",             icon: "", hidden: true },
  { name: "catalogue-manager", label: "Catalogue Manager",    icon: "", hidden: true },
  { name: "product-search",    label: "Product Search",       icon: "", hidden: true },
  { name: "reports",           label: "Reports & Analytics",  icon: "" },
  { name: "payments",          label: "Payments & Billing",   icon: "" },
  { name: "social-ai",         label: "Social Media AI",      icon: "" },
  { name: "settings",          label: "Settings",             icon: "" },
  { name: "help",              label: "Help & Guides",        icon: "", hidden: true },
  { name: "feedback",          label: "Feedback",             icon: "", hidden: true },
];

function makeStub(spec: StubSpec): Page {
  const base: Page = {
    name: spec.name,
    label: spec.label,
    icon: spec.icon,
    mount(container: HTMLElement, _ctx: PageContext): PageCleanup {
      container.innerHTML = `
        <section class="tkos-placeholder">
          <h1>${spec.label}</h1>
          <p>Coming soon — extracting from source-of-truth.</p>
        </section>
      `;
      return () => {
        /* no-op */
      };
    },
  };
  if (spec.hidden) {
    return { ...base, hidden: true };
  }
  return base;
}

// ── Register each stub UNLESS a real page already claimed the name
for (const spec of STUBS) {
  if (getPage(spec.name)) continue; // real page already registered
  registerPage(makeStub(spec));
}

// ── Enforce canonical sidebar order (real pages registered first via imports
// would otherwise appear at the top of the sidebar).
setOrder(STUBS.map((s) => s.name));
